import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Slide,
  useTheme,
  alpha,
  LinearProgress,
  InputAdornment
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SecurityIcon from '@mui/icons-material/Security';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BalanceIcon from '@mui/icons-material/Balance';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import SparklesIcon from '@mui/icons-material/AutoFixHigh';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// Tipos
interface DocumentCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  description: string;
  examples: string[];
}

interface GeneratedDocument {
  id: string;
  document_id: string;
  title: string;
  content: string;
  created_at: string;
  tokens_used: number;
  credits_used: number;
}

const AIDocumentGeneratorPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  
  // Estados principais
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [documentType, setDocumentType] = useState<string>('Petição Inicial');
  const [factsDescription, setFactsDescription] = useState<string>('');
  const [additionalInfo, setAdditionalInfo] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState<GeneratedDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Categorias de documentos
  const documentCategories: DocumentCategory[] = [
    {
      id: 'Cível',
      name: 'Civil',
      icon: <GavelIcon />,
      color: '#1976d2',
      description: 'Contratos, indenizações, responsabilidade civil',
      examples: ['Petição Inicial', 'Contestação', 'Contrato de Locação']
    },
    {
      id: 'Penal',
      name: 'Penal',
      icon: <SecurityIcon />,
      color: '#424242',
      description: 'Defesa criminal, habeas corpus, denúncias',
      examples: ['Habeas Corpus', 'Defesa Prévia', 'Apelação Criminal']
    },
    {
      id: 'Trabalhista',
      name: 'Trabalhista',
      icon: <WorkIcon />,
      color: '#616161',
      description: 'Relações trabalhistas, reclamações',
      examples: ['Reclamação Trabalhista', 'Recurso Ordinário', 'Contestação']
    },
    {
      id: 'Tributário',
      name: 'Tributário',
      icon: <AttachMoneyIcon />,
      color: '#757575',
      description: 'Impostos, taxas, contribuições',
      examples: ['Mandado de Segurança', 'Execução Fiscal', 'Recurso Especial']
    },
    {
      id: 'Família',
      name: 'Família',
      icon: <FamilyRestroomIcon />,
      color: '#9e9e9e',
      description: 'Divórcio, guarda, alimentos',
      examples: ['Ação de Divórcio', 'Guarda de Menores', 'Alimentos']
    },
    {
      id: 'Administrativo',
      name: 'Administrativo',
      icon: <BusinessIcon />,
      color: '#bdbdbd',
      description: 'Licitações, servidores públicos',
      examples: ['Mandado de Segurança', 'Recurso Administrativo', 'Licitação']
    },
    {
      id: 'Previdenciário',
      name: 'Previdenciário',
      icon: <AccountBalanceIcon />,
      color: '#666666',
      description: 'Aposentadoria, benefícios, auxílios',
      examples: ['Aposentadoria', 'Auxílio-Doença', 'Pensão por Morte']
    }
  ];

  // Tipos de documento por categoria
  const documentTypes: Record<string, string[]> = {
    'Cível': ['Petição Inicial', 'Contestação', 'Réplica', 'Contrato', 'Notificação Extrajudicial'],
    'Penal': ['Habeas Corpus', 'Defesa Prévia', 'Apelação Criminal', 'Resposta à Acusação'],
    'Trabalhista': ['Reclamação Trabalhista', 'Contestação', 'Recurso Ordinário', 'Recurso de Revista'],
    'Tributário': ['Mandado de Segurança', 'Execução Fiscal', 'Recurso Especial', 'Contestação'],
    'Família': ['Ação de Divórcio', 'Guarda de Menores', 'Alimentos', 'Investigação de Paternidade'],
    'Administrativo': ['Mandado de Segurança', 'Recurso Administrativo', 'Impugnação', 'Contestação'],
    'Previdenciário': ['Aposentadoria', 'Auxílio-Doença', 'Pensão por Morte', 'Recurso ao INSS']
  };

  const steps = [
    'Escolha a Área do Direito',
    'Selecione o Tipo de Documento',
    'Descreva os Fatos',
    'Gere o Documento'
  ];

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setDocumentType(documentTypes[categoryId]?.[0] || 'Petição Inicial');
    setActiveStep(1);
  };

  const handleGenerateDocument = async () => {
    if (!factsDescription.trim()) {
      setError('Por favor, descreva os fatos do caso');
      return;
    }

    if (factsDescription.length < 50) {
      setError('A descrição dos fatos deve ter pelo menos 50 caracteres');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await api.post('/ai-documents/generate-from-facts', {
        facts_description: factsDescription,
        category: selectedCategory,
        document_type: documentType,
        additional_info: additionalInfo
      });

      if (response.data.status === 'success') {
        setGeneratedDocument(response.data.data);
        setActiveStep(3);
        
        // Redirecionar para preenchimento de campos após 2 segundos
        setTimeout(() => {
          navigate(`/documents/fields/${response.data.data.document_id}`);
        }, 2000);
      } else {
        setError('Erro ao gerar documento. Tente novamente.');
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Erro ao gerar documento');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadDocument = () => {
    if (!generatedDocument) return;

    const element = document.createElement('a');
    const file = new Blob([generatedDocument.content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${generatedDocument.title}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDownloadDocx = async () => {
    if (!generatedDocument) return;

    try {
      const response = await api.get(`/ai-documents/document/${generatedDocument.id}/download-docx`, {
        responseType: 'blob'
      });

      const element = document.createElement('a');
      const file = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      element.href = URL.createObjectURL(file);
      element.download = `${generatedDocument.title}.docx`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      console.error('Erro ao baixar documento Word:', error);
      setError('Erro ao baixar documento Word');
    }
  };

  const renderCategorySelection = () => (
    <Box>
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 2 }}>
        <AutoAwesomeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Como podemos te ajudar hoje?
      </Typography>
      
      <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 4 }}>
        Crie peças jurídicas com o poder da nossa inteligência artificial.
      </Typography>

      {/* Busca por tipo de documento */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Buscar por tipo de documento"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 600,
            mx: 'auto',
            display: 'block',
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.05)
            }
          }}
        />
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          Resultados filtrados enquanto você digita.
        </Typography>
      </Box>

      {/* Seção Em Destaque */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Em destaque
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {documentCategories.map((category) => (
            <Grid item xs={6} md={4} lg={2} key={category.id}>
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  color: '#424242',
                  borderColor: alpha('#424242', 0.3),
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: alpha('#1976d2', 0.05),
                    color: '#1976d2'
                  }
                }}
                onClick={() => handleCategorySelect(category.id)}
              >
                {category.name.replace('Direito ', '')}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Documentos em Destaque */}
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Documentos em destaque
        </Typography>
        
        <Grid container spacing={3}>
          {[
            {
              code: 'PI',
              title: 'Petição Inicial',
              description: 'Redija uma petição inicial robusta em segundos com o auxílio da nossa inteligência artificial.',
              color: '#1976d2',
              category: 'Cível'
            },
            {
              code: 'CT',
              title: 'Contestação',
              description: 'Crie uma contestação jurídica sólida e precisa, totalmente fundamentada, baseada na petição inicial.',
              color: '#424242',
              category: 'Cível'
            },
            {
              code: 'CO',
              title: 'Contratos',
              description: 'Construa contratos de maneira eficaz, formal e ágil, economizando tempo e esforço.',
              color: '#616161',
              category: 'Cível'
            },
            {
              code: 'RE',
              title: 'Réplica',
              description: 'Elabore uma réplica estruturada e persuasiva em resposta à contestação, com fundamentos jurídicos adequados.',
              color: '#757575',
              category: 'Cível'
            },
            {
              code: 'HC',
              title: 'Habeas Corpus',
              description: 'Elabore um habeas corpus fundamentado e tecnicamente correto para garantir a liberdade.',
              color: '#9e9e9e',
              category: 'Penal'
            },
            {
              code: 'RT',
              title: 'Reclamação Trabalhista',
              description: 'Crie uma reclamação trabalhista completa com todos os pedidos e fundamentos legais.',
              color: '#666666',
              category: 'Trabalhista'
            }
          ].map((doc) => (
            <Grid item xs={12} md={6} key={doc.code}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4]
                  },
                  border: `1px solid ${alpha(doc.color, 0.2)}`,
                  borderRadius: 2
                }}
                onClick={() => {
                  setSelectedCategory(doc.category);
                  setDocumentType(doc.title);
                  setActiveStep(2);
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        backgroundColor: alpha(doc.color, 0.1),
                        color: doc.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0
                      }}
                    >
                      <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                        {doc.code}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {doc.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {doc.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );

  const renderDocumentTypeSelection = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Selecione o tipo de documento
      </Typography>
      
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Tipo de Documento</InputLabel>
        <Select
          value={documentType}
          label="Tipo de Documento"
          onChange={(e) => setDocumentType(e.target.value)}
        >
          {documentTypes[selectedCategory]?.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        onClick={() => setActiveStep(2)}
        disabled={!documentType}
        sx={{ mr: 2 }}
      >
        Próximo
      </Button>
      
      <Button
        variant="outlined"
        onClick={() => setActiveStep(0)}
      >
        Voltar
      </Button>
    </Box>
  );

  const renderFactsDescription = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Descreva os fatos do caso
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Informe detalhadamente os fatos em até 3.000 caracteres. Quanto mais completa a descrição, melhor será o documento gerado.
      </Typography>

      <TextField
        multiline
        rows={10}
        fullWidth
        placeholder="Descreva os fatos do caso juridicamente relevantes..."
        value={factsDescription}
        onChange={(e) => setFactsDescription(e.target.value)}
        sx={{ mb: 2 }}
        inputProps={{ maxLength: 3000 }}
      />
      
      <Typography variant="caption" color="text.secondary">
        {factsDescription.length}/3000 caracteres
      </Typography>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleGenerateDocument}
          disabled={!factsDescription.trim() || factsDescription.length < 50 || isGenerating}
          startIcon={isGenerating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
          sx={{ mr: 2 }}
        >
          {isGenerating ? 'Gerando documento...' : 'Gerar Documento'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={() => setActiveStep(1)}
          disabled={isGenerating}
        >
          Voltar
        </Button>
      </Box>
    </Box>
  );

  const renderGeneratedDocument = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Documento Gerado com Sucesso!
      </Typography>
      
      {generatedDocument && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {generatedDocument.title}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Gerado em: {new Date(generatedDocument.created_at).toLocaleString('pt-BR')}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Tokens usados: {generatedDocument.tokens_used} | Créditos consumidos: {generatedDocument.credits_used}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/documents/fields/${generatedDocument.document_id}`)}
              color="primary"
              size="large"
            >
              Preencher Campos
            </Button>
            
            <Button
              variant="contained"
              startIcon={<PreviewIcon />}
              onClick={() => setPreviewOpen(true)}
              color="secondary"
            >
              Visualizar
            </Button>
            
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadDocx}
              sx={{ backgroundColor: '#1976d2' }}
            >
              Baixar .docx
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadDocument}
            >
              Baixar .txt
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
            Você será redirecionado automaticamente para preencher os campos em alguns segundos...
          </Typography>
        </Paper>
      )}
      
      <Button
        variant="outlined"
        onClick={() => {
          setActiveStep(0);
          setSelectedCategory('');
          setDocumentType('Petição Inicial');
          setFactsDescription('');
          setGeneratedDocument(null);
          setError(null);
        }}
      >
        Gerar Novo Documento
      </Button>
    </Box>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderCategorySelection();
      case 1:
        return renderDocumentTypeSelection();
      case 2:
        return renderFactsDescription();
      case 3:
        return renderGeneratedDocument();
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isGenerating && (
        <Paper 
          sx={{ 
            p: 4, 
            mb: 3, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.02) 0%, rgba(52, 152, 219, 0.05) 100%)'
          }}
        >
          {/* Logo Animado */}
          <Box
            sx={{
              position: 'relative',
              width: 100,
              height: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            {/* Anel pulsante */}
            <Box
              sx={{
                position: 'absolute',
                width: 100,
                height: 100,
                borderRadius: '50%',
                border: '3px solid',
                borderColor: 'primary.main',
                opacity: 0.3,
                animation: 'pulseRing 2s ease-out infinite',
                '@keyframes pulseRing': {
                  '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.2 },
                  '100%': { transform: 'scale(0.8)', opacity: 0.5 },
                },
              }}
            />
            {/* Logo flutuante */}
            <Box
              component="img"
              src="/favicon.svg"
              alt="Logo Advogada Parceira"
              sx={{
                width: 60,
                height: 60,
                animation: 'logoFloat 2s ease-in-out infinite',
                '@keyframes logoFloat': {
                  '0%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-8px)' },
                  '100%': { transform: 'translateY(0px)' },
                },
              }}
            />
          </Box>
          
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Gerando documento com IA...
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
            Nossa inteligência artificial está elaborando seu documento com legislação e jurisprudência atualizadas.
          </Typography>
          
          {/* Barra de progresso sutil */}
          <LinearProgress 
            sx={{ 
              width: '60%', 
              mt: 3, 
              borderRadius: 2,
              height: 6,
              backgroundColor: 'rgba(0,0,0,0.05)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
              }
            }} 
          />
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        {activeStep > 0 && (
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {renderStepContent(activeStep)}
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {generatedDocument?.title}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9rem' }}>
            {generatedDocument?.content}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            Fechar
          </Button>
          <Button variant="contained" onClick={handleDownloadDocument}>
            Baixar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AIDocumentGeneratorPage; 